import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Zap, Phone, Clock, TrendingDown, AlertCircle,
  ChevronLeft, ChevronRight, RefreshCw, Bot, Mic,
  Calculator, ExternalLink, Info,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  whisper: "#f59e0b",
  gpt: "#3b82f6",
  callAnalytics: "#f59e0b",
  otherUsage: "#8b5cf6",
  valid: "#10b981",
  spam: "#ef4444",
  adult: "#f43f5e",
  dead: "#6b7280",
  other: "#8b5cf6",
};

function StatCard({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <Card className="bg-zinc-900/80 border-zinc-800" data-testid={testId}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg" style={{ backgroundColor: `${color}15` }}>
            <Icon className="h-5 w-5" style={{ color }} />
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-white mt-0.5">{value}</p>
            {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EmergentCreditsPage() {
  const [dailyData, setDailyData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [totalCreditsInput, setTotalCreditsInput] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [dailyRes, weeklyRes] = await Promise.all([
        axios.get(`${API}/emergent-credits/daily?date=${selectedDate}`, { headers }),
        axios.get(`${API}/emergent-credits/weekly`, { headers }),
      ]);
      setDailyData(dailyRes.data);
      setWeeklyData(weeklyRes.data);
    } catch (err) {
      console.error("Failed to fetch credit data:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load saved total credits from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`emergent_total_credits_${selectedDate}`);
    if (saved) setTotalCreditsInput(saved);
    else setTotalCreditsInput("");
  }, [selectedDate]);

  const navigateDate = (dir) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + dir);
    if (d <= new Date()) setSelectedDate(d.toISOString().split("T")[0]);
  };

  const saveTotalCredits = () => {
    if (totalCreditsInput) {
      localStorage.setItem(`emergent_total_credits_${selectedDate}`, totalCreditsInput);
    }
  };

  const isToday = selectedDate === new Date().toISOString().split("T")[0];
  const ca = dailyData?.call_analytics;
  const callAnalyticsCredits = ca?.total_credits || 0;
  const totalInputNum = parseFloat(totalCreditsInput) || 0;
  const otherCredits = totalInputNum > callAnalyticsCredits ? +(totalInputNum - callAnalyticsCredits).toFixed(4) : 0;
  const callPct = totalInputNum > 0 ? ((callAnalyticsCredits / totalInputNum) * 100).toFixed(1) : 0;
  const otherPct = totalInputNum > 0 ? ((otherCredits / totalInputNum) * 100).toFixed(1) : 0;

  const splitPieData = totalInputNum > 0 ? [
    { name: "Call Analytics", value: callAnalyticsCredits, color: COLORS.callAnalytics },
    { name: "Other (Agent/Edits)", value: otherCredits, color: COLORS.otherUsage },
  ].filter(d => d.value > 0) : [];

  const costPieData = ca ? [
    { name: "Whisper", value: ca.breakdown?.whisper_transcription || 0, color: COLORS.whisper },
    { name: "GPT-4o-mini", value: ca.breakdown?.gpt4o_mini_analysis || 0, color: COLORS.gpt },
  ].filter(d => d.value > 0) : [];

  const categoryData = ca ? [
    { name: "Valid", count: ca.by_category?.valid_service_call || 0, color: COLORS.valid },
    { name: "Spam", count: ca.by_category?.spam_call || 0, color: COLORS.spam },
    { name: "Adult", count: ca.by_category?.adult_call || 0, color: COLORS.adult },
    { name: "Dead", count: ca.by_category?.dead_call || 0, color: COLORS.dead },
    { name: "Other", count: ca.by_category?.other || 0, color: COLORS.other },
  ].filter(d => d.count > 0) : [];

  const weeklyChartData = weeklyData?.days?.slice().reverse() || [];

  if (loading && !dailyData) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="credits-loading">
        <RefreshCw className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="emergent-credits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="credits-page-title">Emergent Credits Expenses</h1>
          <p className="text-sm text-zinc-400 mt-1">Track your AI credit consumption across all services</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)} data-testid="date-prev-btn" className="text-zinc-400 hover:text-white">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-3 py-1.5 bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-sm text-zinc-300 min-w-[140px] text-center" data-testid="selected-date">
            {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
          </div>
          <Button variant="ghost" size="icon" onClick={() => navigateDate(1)} disabled={isToday} data-testid="date-next-btn" className="text-zinc-400 hover:text-white disabled:opacity-30">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} className="ml-2 border-zinc-700 text-zinc-300" data-testid="refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 1: CALL ANALYTICS USAGE */}
      {/* ============================================ */}
      <Card className="bg-zinc-900/80 border-zinc-800" data-testid="call-analytics-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-zinc-200 flex items-center gap-2">
            <Phone className="h-5 w-5 text-amber-400" />
            1. Call Analytics Usage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Credits Used" value={callAnalyticsCredits.toFixed(2)} icon={Zap} color="#f59e0b" testId="total-credits-card" />
            <StatCard label="Calls Analyzed" value={ca?.total_calls_analyzed || 0} icon={Phone} color="#3b82f6" testId="calls-analyzed-card" />
            <StatCard label="Audio Processed" value={`${ca?.total_audio_minutes || 0} min`} icon={Clock} color="#a855f7" testId="audio-minutes-card" />
            <StatCard label="Avg / Call" value={(ca?.avg_credits_per_call || 0).toFixed(4)} icon={TrendingDown} color="#10b981" testId="avg-per-call-card" />
          </div>

          {/* Whisper & GPT Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 grid grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-amber-500/5 border border-amber-500/10">
                <div className="flex items-center gap-2 mb-1.5">
                  <Mic className="h-4 w-4 text-amber-400" />
                  <span className="text-xs text-amber-400/80 uppercase tracking-wider">Whisper Transcription</span>
                </div>
                <p className="text-xl font-semibold text-white" data-testid="whisper-credits">
                  {(ca?.breakdown?.whisper_transcription || 0).toFixed(4)} <span className="text-xs text-zinc-500">credits</span>
                </p>
                <p className="text-xs text-zinc-500 mt-1">{ca?.total_audio_minutes || 0} min @ 0.006/min</p>
              </div>
              <div className="p-3.5 rounded-lg bg-blue-500/5 border border-blue-500/10">
                <div className="flex items-center gap-2 mb-1.5">
                  <Bot className="h-4 w-4 text-blue-400" />
                  <span className="text-xs text-blue-400/80 uppercase tracking-wider">GPT-4o-mini Analysis</span>
                </div>
                <p className="text-xl font-semibold text-white" data-testid="gpt-credits">
                  {(ca?.breakdown?.gpt4o_mini_analysis || 0).toFixed(4)} <span className="text-xs text-zinc-500">credits</span>
                </p>
                <p className="text-xs text-zinc-500 mt-1">{ca?.total_calls_analyzed || 0} calls @ 0.002/call</p>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="space-y-2">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Calls by Category</p>
              {categoryData.map((cat) => (
                <div key={cat.name} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                    <span className="text-sm text-zinc-300">{cat.name}</span>
                  </div>
                  <Badge variant="secondary" className="bg-zinc-800 text-zinc-300 text-xs">{cat.count}</Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Hourly Chart */}
          {ca?.hourly?.length > 0 && (
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Hourly Credit Usage</p>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ca.hourly} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="hour" tick={{ fill: "#71717a", fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#71717a", fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px", fontSize: "12px" }}
                      formatter={(v, n) => [n === "credits" ? `${v.toFixed(4)} credits` : `${v} calls`, n === "credits" ? "Credits" : "Calls"]}
                    />
                    <Bar dataKey="credits" fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={20} name="credits" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Failed calls */}
          {ca?.failed_calls?.count > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/5 border border-red-500/10">
              <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
              <p className="text-xs text-zinc-400">
                <span className="text-red-300 font-medium">{ca.failed_calls.count} calls failed</span> transcription, wasting <span className="text-red-300 font-medium">{ca.failed_calls.wasted_credits.toFixed(4)} credits</span> on retries.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============================================ */}
      {/* SECTION 2: OTHER USAGE (Non-Call Analytics) */}
      {/* ============================================ */}
      <Card className="bg-zinc-900/80 border-zinc-800" data-testid="other-usage-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-zinc-200 flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-400" />
            2. Other Usage (Non-Call Analytics)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3.5 rounded-lg bg-purple-500/5 border border-purple-500/10">
              <p className="text-xs text-purple-400/80 uppercase tracking-wider mb-1.5 font-medium">Agent Chat Sessions</p>
              <p className="text-sm text-zinc-300">Each message you send to the Emergent AI agent uses credits. Includes code changes, screenshots, tests, deployments.</p>
            </div>
            <div className="p-3.5 rounded-lg bg-indigo-500/5 border border-indigo-500/10">
              <p className="text-xs text-indigo-400/80 uppercase tracking-wider mb-1.5 font-medium">System Changes & Edits</p>
              <p className="text-sm text-zinc-300">Credits used by the agent for editing code, running tools, debugging, and deploying updates to your app.</p>
            </div>
            <div className="p-3.5 rounded-lg bg-cyan-500/5 border border-cyan-500/10">
              <p className="text-xs text-cyan-400/80 uppercase tracking-wider mb-1.5 font-medium">Prompts & Testing</p>
              <p className="text-sm text-zinc-300">Credits for automated test runs, screenshot captures, and any AI-powered prompts outside call analysis.</p>
            </div>
          </div>

          {/* Credit Calculator */}
          <div className="p-4 rounded-lg bg-zinc-800/60 border border-zinc-700/40" data-testid="credit-calculator">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="h-4 w-4 text-amber-400" />
              <p className="text-sm font-medium text-zinc-200">Credit Split Calculator</p>
              <div className="group relative">
                <Info className="h-3.5 w-3.5 text-zinc-500 cursor-help" />
                <div className="hidden group-hover:block absolute z-10 bottom-6 left-0 w-72 p-2 bg-zinc-800 border border-zinc-600 rounded-lg text-xs text-zinc-300 shadow-lg">
                  Enter your total credits used today from the Emergent Platform (Profile &rarr; Universal Key &rarr; Usage History). We'll calculate how much went to Call Analytics vs Other.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-xs text-zinc-500 block mb-1">Total Credits Used Today (from Emergent Platform)</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 5.50"
                    value={totalCreditsInput}
                    onChange={(e) => setTotalCreditsInput(e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-white h-9 max-w-[200px]"
                    data-testid="total-credits-input"
                  />
                  <Button size="sm" onClick={saveTotalCredits} className="bg-amber-600 hover:bg-amber-700 text-white h-9" data-testid="save-credits-btn">
                    Calculate
                  </Button>
                </div>
              </div>

              {totalInputNum > 0 && (
                <div className="flex gap-6 items-center">
                  <div className="text-center">
                    <p className="text-xs text-amber-400/80">Call Analytics</p>
                    <p className="text-lg font-bold text-amber-400" data-testid="calc-analytics-credits">{callAnalyticsCredits.toFixed(2)}</p>
                    <p className="text-xs text-zinc-500">{callPct}%</p>
                  </div>
                  <div className="h-10 w-px bg-zinc-700" />
                  <div className="text-center">
                    <p className="text-xs text-purple-400/80">Other Usage</p>
                    <p className="text-lg font-bold text-purple-400" data-testid="calc-other-credits">{otherCredits.toFixed(2)}</p>
                    <p className="text-xs text-zinc-500">{otherPct}%</p>
                  </div>
                  <div className="h-10 w-px bg-zinc-700" />
                  <div className="text-center">
                    <p className="text-xs text-zinc-400">Total</p>
                    <p className="text-lg font-bold text-white" data-testid="calc-total-credits">{totalInputNum.toFixed(2)}</p>
                    <p className="text-xs text-zinc-500">100%</p>
                  </div>
                </div>
              )}
            </div>

            {/* Split Pie Chart */}
            {splitPieData.length > 0 && (
              <div className="mt-4 h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={splitPieData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} paddingAngle={4} dataKey="value">
                      {splitPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px", fontSize: "12px" }}
                      formatter={(v) => [`${v.toFixed(4)} credits`]}
                    />
                    <Legend wrapperStyle={{ fontSize: "11px" }} formatter={(v) => <span style={{ color: "#a1a1aa" }}>{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Where to check */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/40 border border-zinc-700/30">
            <ExternalLink className="h-4 w-4 text-cyan-400 shrink-0" />
            <p className="text-xs text-zinc-400">
              To see exact credit usage for agent sessions, go to <span className="text-cyan-400 font-medium">Emergent Platform &rarr; Profile &rarr; Universal Key &rarr; Usage History</span>
            </p>
          </div>

          <div className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700/30">
            <p className="text-xs text-zinc-400">
              <span className="text-zinc-300 font-medium">Scheduler Info:</span> Auto-import ran <span className="text-white font-semibold">{dailyData?.other_usage?.scheduler_runs_today || 0}</span> times today.
              Empty runs (no new calls) cost <span className="text-green-400 font-medium">0 credits</span> — only Ringba's API is checked, no AI is used.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ============================================ */}
      {/* SECTION 3: SUMMARY */}
      {/* ============================================ */}
      <Card className="bg-gradient-to-r from-zinc-900/90 to-zinc-800/50 border-zinc-700/50" data-testid="summary-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-zinc-200 flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-green-400" />
            3. Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700/30 text-center">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Credits Today</p>
              <p className="text-3xl font-bold text-white" data-testid="summary-total">
                {totalInputNum > 0 ? totalInputNum.toFixed(2) : callAnalyticsCredits.toFixed(2)}
              </p>
              <p className="text-xs text-zinc-500 mt-1">{totalInputNum > 0 ? "From your input" : "Call Analytics only"}</p>
            </div>
            <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/10 text-center">
              <p className="text-xs text-amber-400/80 uppercase tracking-wider mb-1">Call Analytics</p>
              <p className="text-3xl font-bold text-amber-400" data-testid="summary-analytics">{callAnalyticsCredits.toFixed(2)}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {totalInputNum > 0 ? `${callPct}% of total` : `${ca?.total_calls_analyzed || 0} calls analyzed`}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/10 text-center">
              <p className="text-xs text-purple-400/80 uppercase tracking-wider mb-1">Other Usage</p>
              <p className="text-3xl font-bold text-purple-400" data-testid="summary-other">
                {totalInputNum > 0 ? otherCredits.toFixed(2) : "—"}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {totalInputNum > 0 ? `${otherPct}% of total` : "Enter total credits above"}
              </p>
            </div>
          </div>
          <div className="mt-3 p-2.5 rounded-lg bg-zinc-800/40 text-center">
            <p className="text-xs text-zinc-500">
              Pipeline: <span className="text-zinc-400">{dailyData?.summary?.pipeline_info || "Whisper + GPT-4o-mini"}</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ============================================ */}
      {/* 7-DAY TREND */}
      {/* ============================================ */}
      {weeklyChartData.length > 0 && (
        <Card className="bg-zinc-900/80 border-zinc-800" data-testid="weekly-trend-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-zinc-200">7-Day Credit Trend (Call Analytics)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyChartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="day_label" tick={{ fill: "#71717a", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px", fontSize: "12px" }}
                    formatter={(v, n) => [n === "credits" ? `${v.toFixed(4)} credits` : `${v} calls`, n === "credits" ? "Credits" : "Calls"]}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px" }} formatter={(v) => <span style={{ color: "#a1a1aa" }}>{v === "credits" ? "Credits" : "Calls"}</span>} />
                  <Bar dataKey="credits" fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={28} name="credits" />
                  <Bar dataKey="calls" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={28} name="calls" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
